import * as React from "react";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { Grid, IconButton, OutlinedInput } from "@mui/material";

import InputAdornment from "@mui/material/InputAdornment";

import "./PopOverLinkCopyButton.scss";
import { QRCode } from "react-qrcode-logo";

import html2canvas from "html2canvas";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Link as LinkIcon, Copy as ContentCopyIcon, Reply as ReplyIcon, Check as DoneIcon, Crop as CropFreeIcon, Download as DownloadIcon } from "lucide-react";

/** navigator.clipboard is undefined outside a secure context, e.g. admin on a phone over a LAN IP. */
async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the selection path */
  }
  try {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.cssText = "position:fixed;top:0;left:0;opacity:0";
    document.body.appendChild(field);
    field.select();
    field.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(field);
    return copied;
  } catch {
    return false;
  }
}

export default function PopOverLinkCopyButton({
  url,
  title,
  fullWidthRow,
}: {
  title?: string;
  url: string | undefined;
  /** Renders the trigger as a flat, full-width, left-aligned row (for a divided action-sheet list) instead of the default small icon button. */
  fullWidthRow?: boolean;
}) {
  /** react-qrcode-logo crashes if value is null/undefined (reads .length). */
  const safeUrl = url == null || url === "" ? "" : String(url);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [copied, setCopied] = React.useState(false);

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const handleLinkCopy = async () => {
    if (!safeUrl) return;
    if (!(await copyText(safeUrl))) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    html2canvas(document.querySelector("#react-qrcode-logo") as any).then(
      function (canvas) {
        const link = document.createElement("a");
        link.download = "react-qrcode-logo.png";
        link.href = canvas.toDataURL();
        link.click();
      }
    );
  };

  return (
    <Grid>
      {fullWidthRow ? (
        <button
          type="button"
          aria-describedby={id}
          onClick={handleClick}
          className="flex h-11 w-full items-center gap-2.5 px-6 text-sm font-normal text-foreground transition-colors hover:bg-muted"
        >
          <LinkIcon className="size-3.5" />
          {title}
        </button>
      ) : (
        <IconButton
          aria-describedby={id}
          onClick={handleClick}
          size={"small"}
          sx={{
            height: "30px",
            px: 1,
            gap: "6px",
            border: "var(--cuo-border-soft)",
            borderRadius: "5px",
            background: "white",
            "&:hover": {
              background: "white",
              border: "var(--cuo-border)",
            },
          }}
        >
          <LinkIcon style={{ width: 14, height: 14 }} />
          {title && (
            <Typography display={{ xs: "none", md: "flex" }} fontSize={"13px"}>
              {title}
            </Typography>
          )}
        </IconButton>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{
          mt: 1,
          ".MuiPopover-paper": {
            width: "300px",
            border: "var(--cuo-border)",
            boxShadow: "var(--cuo-box-shadow)",
            borderRadius: "5px",
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Grid container direction={"column"} p={1.5} pt={0.5}>
          <Grid container alignItems={"center"}>
            <Typography>Share link</Typography>
            <ReplyIcon
              style={{ width: "15px", height: "15px", transform: "scaleX(-1)", padding: "8px" }}
            />
          </Grid>

          {!safeUrl ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No link is available yet. Save a ticket URL, checkout link, or
              storefront URL for this item, then open this again.
            </Typography>
          ) : (
            <>
              <OutlinedInput
                disabled
                sx={{
                  input: {
                    cursor: "text",
                  },
                  fontSize: "13px",
                }}
                size={"small"}
                value={safeUrl}
                id="outlined-adornment-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="Copy link"
                      onClick={() => handleLinkCopy()}
                      edge="end"
                    >
                      {copied ? (
                        <DoneIcon style={{ color: "#4bb543", width: "15px", height: "15px" }} />
                      ) : (
                        <ContentCopyIcon
                          style={{ color: "var(--cuo-primary)", width: "15px", height: "15px" }}
                        />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <Grid container direction={"column"} pt={2}>
                <Grid container alignItems={"center"}>
                  <Typography>QR Code</Typography>
                  <CropFreeIcon
                    style={{ width: "15px", height: "15px", transform: "scaleX(-1)", padding: "8px" }}
                  />
                </Grid>
                <Grid container justifyContent={"center"} rowGap={2}>
                  <QRCode size={225} value={safeUrl} />
                  <CuoButton
                    className="w-full"
                    onClick={handleDownload}
                    icon={<DownloadIcon style={{ marginRight: "8px" }} />}
                    label={"Download QR Code"}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Popover>
    </Grid>
  );
}
